import LinkToPost from "components/engagement/notifications/elements/links/LinkToPost";
import NotificationListItem from "components/engagement/notifications/items/NotificationListItem";
import * as React from "react";
import { useIntl } from "react-intl";
import Avatar from "../../../core/Avatar";
import CommentTime from "../../../discussions/CommentTime";
import NotificationActors from "../elements/NotificationActors";
import NotificationContentParagraph from "../elements/NotificationContentParagraph";
import SafeActorName from "../elements/SafeActorName";
import type { AlsoRepliedToPostNotification } from "../types";
import getActivityTimeAsUtc from "../utils/getActivityTimeAsUtc";
import pullActorsFromActivities from "../utils/pullActorsFromActivities";
import safeCommentOrReplyPreamble from "../utils/safeCommentOrReplyPreamble";

interface IAlsoRepliedToPostNotificationListItemProps extends React.HTMLAttributes<HTMLLIElement> {
  notification: AlsoRepliedToPostNotification;
}

export default function AlsoRepliedToPostNotificationListItem({
  notification,
}: IAlsoRepliedToPostNotificationListItemProps) {
  const intl = useIntl();

  // Shouldn't happen but just making sure so it doesn't break when people are using it.
  if (notification.activities.length === 0) {
    return null;
  }

  const topActivity = notification.activities[0];
  // When the author of the post replies to its own post
  const repliedToSelfPostSentence = intl.formatMessage({
    defaultMessage: "respondeu ao comentário",
    description: "Notificações",
    id: "hE3eeA",
  });
  // When others replies to someone else's post
  const repliedToSomeoneElsePostSentence = intl.formatMessage({
    defaultMessage: "também respondeu ao comentário de",
    description: "Notificações",
    id: "9C6A5t",
  });
  // Tells whether it's the owner of the post also replying to its own post
  const isOwnPost = topActivity.actor.id === topActivity.postAuthor.id;
  const replyPreamble = safeCommentOrReplyPreamble(topActivity.reply.data.bodyAsPlainText);
  const actors = (
    <NotificationActors
      maximumActorsConjunction={3}
      actors={pullActorsFromActivities(notification.activities).map((actor) => {
        return <SafeActorName key={actor.id} firstName={actor.data.firstName} />;
      })}
    />
  );

  return (
    <NotificationListItem notification={notification}>
      <Avatar
        src={topActivity.actor.data.pictureUrl}
        hashValue={topActivity.actor.id}
        alt={topActivity.actor.data.firstName}
        size={42}
        squircle={true}
      />

      <div className="grid w-72 gap-y-2 lg:w-full">
        <NotificationContentParagraph>
          {isOwnPost || !topActivity.postAuthor.data ? (
            <LinkToPost activity={topActivity}>
              {actors} {repliedToSelfPostSentence} <i>{`"${replyPreamble}"`}</i>
            </LinkToPost>
          ) : (
            <LinkToPost activity={topActivity}>
              {actors} {repliedToSomeoneElsePostSentence}{" "}
              <SafeActorName firstName={topActivity.postAuthor.data.firstName} /> <i>{`"${replyPreamble}"`}</i>
            </LinkToPost>
          )}
        </NotificationContentParagraph>

        <div className="text-right">
          <LinkToPost activity={topActivity}>
            <CommentTime timeStamp={getActivityTimeAsUtc(topActivity.time)} />
          </LinkToPost>
        </div>
      </div>
    </NotificationListItem>
  );
}
